// ─────────────────────────────────────────────────────────────
// List + manage payment links. Copy/share URL, view totals,
// deactivate, delete. Wrap in <ServiceRoute serviceKey="payment_link">.
//
// LIGHT/DARK MODE PASS — migrated to the design-token system, same
// pass as CreatePaymentLinkPage.jsx. STATUS_STYLE previously hardcoded
// raw Tailwind color CLASSES per status (bg-emerald-100 text-emerald-
// 700, etc.) — switched to the semantic tone tokens (success/info/
// muted) already used for status pills elsewhere in this app (e.g.
// TicketList.jsx's status badges), so these correctly invert in dark
// mode instead of staying fixed light-mode colors regardless of theme.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { useNavigate }          from "react-router-dom";
import { toast }                from "react-toastify";
import { apiClient }            from "../../api/apiServices";

// FIXED — was raw Tailwind classes (bg-emerald-100 text-emerald-700,
// etc.), fixed light-mode colors with no dark-mode equivalent. Now
// references the semantic tone tokens, applied inline via style (same
// pattern as TicketList.jsx's STATUS_CONFIG) so each tone's light/
// dark pairing is handled by the token system itself.
const STATUS_STYLE = {
  active:      { label: "Active",  tone: "success" },
  paid:        { label: "Paid",    tone: "info" },
  expired:     { label: "Expired", tone: "muted" },
  deactivated: { label: "Off",     tone: "muted" },
};

export default function MyPaymentLinksPage() {
  const navigate = useNavigate();
  const [links, setLinks]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied]   = useState(null);

  const load = () => {
    apiClient.get("/mtrade/payment-links")
      .then(r => { if (r.data.status) setLinks(r.data.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const copy = async (url, id) => {
    await navigator.clipboard.writeText(url).catch(() => {});
    setCopied(id); setTimeout(() => setCopied(null), 2000);
  };

  const share = (link) => {
    navigator.share?.({ title: link.title || "Pay me", url: link.url }).catch(() => {});
  };

  const deactivate = async (id) => {
    try { await apiClient.patch(`/mtrade/payment-links/${id}/deactivate`); toast.success("Deactivated"); load(); }
    catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this link? This cannot be undone.")) return;
    try { await apiClient.delete(`/mtrade/payment-links/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="min-h-screen bg-bg pb-28 font-display">
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border">
        <div className="flex items-center justify-between max-w-3xl mx-auto">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
              <i className="ri-arrow-left-line text-secondary text-lg" />
            </button>
            <h1 className="text-primary font-bold text-lg">Payment Links</h1>
          </div>
          <button onClick={() => navigate("/payment-link/new")}
            className="flex items-center gap-1.5 bg-brand hover:bg-brand-hover text-on-brand text-xs font-bold px-3 py-2 rounded-xl transition-colors">
            <i className="ri-add-line" />New
          </button>
        </div>
      </div>

      <div className="px-5 pt-5 max-w-3xl mx-auto space-y-3">
        {loading ? (
          [1, 2].map(i => <div key={i} className="h-28 bg-skeleton rounded-2xl animate-pulse" />)
        ) : links.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-brand-light rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-link text-brand text-2xl" />
            </div>
            <p className="text-primary font-bold text-sm">No payment links yet</p>
            <p className="text-muted text-xs mt-1 mb-4">Create a link to start receiving money.</p>
            <button onClick={() => navigate("/payment-link/new")}
              className="bg-brand hover:bg-brand-hover text-on-brand font-bold text-sm px-5 py-3 rounded-2xl transition-colors">
              Create Your First Link
            </button>
          </div>
        ) : (
          links.map(link => {
            const st = STATUS_STYLE[link.status] || STATUS_STYLE.active;
            return (
              <div key={link._id} className="bg-surface border border-border rounded-2xl p-4 shadow-card">
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0">
                    <p className="text-primary font-semibold text-sm truncate">{link.title || "Untitled link"}</p>
                    <p className="text-muted text-xs">
                      {link.amount != null ? `₦${Number(link.amount).toLocaleString()}` : "Open amount"} · {link.reusable ? "Reusable" : "One-time"}
                    </p>
                  </div>
                  <span
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                    style={{
                      background: st.tone === "muted" ? "var(--color-surface-hover)" : `var(--color-${st.tone}-light)`,
                      color:      st.tone === "muted" ? "var(--color-text-muted)" : `var(--color-${st.tone}-text)`,
                    }}
                  >
                    {st.label}
                  </span>
                </div>

                <div className="flex items-center gap-2 bg-surface-muted rounded-xl px-3 py-2 mb-2">
                  <span className="text-muted text-xs font-mono flex-1 truncate">{link.url}</span>
                  <button onClick={() => copy(link.url, link._id)} className="text-brand text-xs font-semibold flex-shrink-0">
                    {copied === link._id ? "Copied!" : "Copy"}
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-muted text-xs">
                    Received <span className="font-bold text-primary">₦{Number(link.totalReceived || 0).toLocaleString()}</span> · {link.paymentCount || 0} payment(s)
                  </p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => share(link)} className="w-8 h-8 rounded-lg bg-surface-hover flex items-center justify-center">
                      <i className="ri-share-line text-secondary text-sm" />
                    </button>
                    {link.status === "active" && (
                      <button onClick={() => deactivate(link._id)} className="w-8 h-8 rounded-lg bg-warning-light flex items-center justify-center">
                        <i className="ri-pause-line text-sm" style={{ color: "var(--color-warning)" }} />
                      </button>
                    )}
                    <button onClick={() => remove(link._id)} className="w-8 h-8 rounded-lg bg-danger-light flex items-center justify-center">
                      <i className="ri-delete-bin-line text-sm" style={{ color: "var(--color-danger)" }} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}